import React from "react";
import { useMember } from "../context/MemberContext";

/**
 * Wraps member-only pages.
 *
 * Members only ever arrive here via a signed handoff link from the Angel
 * Squad app. If we have no member identity and no token to verify, we now
 * show a graceful landing screen instead of redirecting — there is no
 * separate "Hub" to send users back to, and a window.location.replace
 * to ourselves (the previous behaviour) caused an infinite flicker loop.
 */
export default function MemberGate({ children }) {
    const { member, identifyError, clear } = useMember();

    // Loading: handoff in progress.
    if (member === null) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="member-loading">
                <div className="font-poetic text-[#9a958a] text-lg">a moment of stillness…</div>
            </div>
        );
    }

    // No identity → show the graceful landing screen.
    if (member === false) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6" data-testid="member-gate-empty">
                <div className="max-w-md text-center">
                    <span className="font-label text-[11px] tracking-[0.25em] text-[#cfa052] uppercase">
                        Angel Squad · private portal
                    </span>
                    <h1
                        className="mt-4 font-serif-display text-4xl md:text-5xl tracking-tighter text-[#e8e4db]"
                        data-testid="member-gate-title"
                    >
                        Open me from your <span className="italic">Squad app</span>.
                    </h1>
                    <p className="mt-6 font-poetic text-[#9a958a] text-base leading-relaxed">
                        This portal is for paid Squad members. To enter, open the Angel Squad Inspirations app
                        on your phone and tap <span className="text-[#e8e4db]">My Angels</span> — that link will
                        bring you straight here, signed-in.
                    </p>
                    {identifyError && (
                        <p
                            className="mt-6 font-label text-[10px] tracking-[0.25em] uppercase text-rose-400/80"
                            data-testid="member-gate-error"
                        >
                            {identifyError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={clear}
                        className="mt-10 font-label text-[10px] tracking-[0.25em] uppercase text-[#6e6a62] hover:text-[#cfa052] transition-colors"
                        data-testid="member-gate-reset"
                    >
                        clear stored identity
                    </button>
                </div>
            </div>
        );
    }

    return children;
}
